import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["story", "progressSegment", "shareButton"]
  static values = {
    currentIndex: Number,
    totalStories: Number,
    defaultAutoAdvanceDelay: { type: Number, default: 5000 }
  }

  autoAdvanceTimer = null
  shareButtonTimer = null
  visitedStories = new Set()
  requiresInteraction = false // Track if current story needs user interaction before advancing

  // Hold-to-pause state
  isPaused = false
  isPausedForShare = false // Paused indefinitely for native share
  holdDetectionTimer = null
  holdStartTime = null
  progressStartTime = null
  remainingTime = null
  pausedProgressWidth = null
  holdThreshold = 150 // ms before hold is detected
  wasHoldingBeforeRelease = false // Track if we were holding to prevent click navigation

  // Get the auto-advance delay for the current story (or default)
  getCurrentAutoAdvanceDelay() {
    const currentStory = this.storyTargets[this.currentIndexValue]
    if (currentStory && currentStory.dataset.autoAdvanceDelay) {
      return parseInt(currentStory.dataset.autoAdvanceDelay, 10)
    }
    return this.defaultAutoAdvanceDelayValue
  }

  connect() {
    // Setup keyboard navigation
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)

    // Setup visibility change handler to pause/resume when tab is hidden
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange)

    // Setup touch gesture support
    this.setupTouchGestures()

    // Mark initial story as visited
    this.visitedStories.add(this.currentIndexValue)

    // Show initial story
    this.showCurrentStory()

    // Update initial progress
    this.updateProgress()

    // Start auto-advance
    this.startAutoAdvance()

    // Schedule share button reveal for initial story
    this.scheduleShareButtonReveal()

    // Track that user opened their year in review
    this.trackEvent("opened")
  }

  disconnect() {
    // Cleanup event listeners
    document.removeEventListener("keydown", this.boundHandleKeydown)
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange)
    this.stopAutoAdvance()
    this.cancelShareButtonReveal()
    this.cancelHoldDetection()
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Tab is hidden - pause everything
      this.stopAutoAdvance()
      this.cancelShareButtonReveal()
    } else {
      // Tab is visible again - reset the current story display and restart
      // But only if we're not paused for share
      if (!this.isPausedForShare) {
        this.showCurrentStory(true) // Force show as revisit to avoid animation issues
        this.updateProgress()
        this.startAutoAdvance()
        this.showShareButton() // Show share button immediately on return
      }
    }
  }

  handleKeydown(event) {
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.exit()
    }
  }

  setupTouchGestures() {
    let touchStartX = 0
    let touchStartY = 0
    let touchEndX = 0

    // Touch events for mobile
    this.element.addEventListener("touchstart", (e) => {
      // If paused for share, don't start hold detection
      if (this.isPausedForShare) return

      touchStartX = e.changedTouches[0].screenX
      touchStartY = e.changedTouches[0].screenY
      this.startHoldDetection(e)
    })

    this.element.addEventListener("touchmove", (e) => {
      // Cancel hold if user moves significantly (they're swiping)
      const moveX = Math.abs(e.changedTouches[0].screenX - touchStartX)
      const moveY = Math.abs(e.changedTouches[0].screenY - touchStartY)
      if (moveX > 10 || moveY > 10) {
        this.cancelHoldDetection()
      }
    })

    this.element.addEventListener("touchend", (e) => {
      // If paused for share, tap to resume
      if (this.isPausedForShare) {
        this.handleTapToResume(e)
        return
      }

      touchEndX = e.changedTouches[0].screenX
      const wasHolding = this.isPaused

      this.endHoldDetection()

      // Only handle swipe if we weren't holding
      if (!wasHolding) {
        this.handleSwipe(touchStartX, touchEndX)
      }
    })

    this.element.addEventListener("touchcancel", () => {
      this.endHoldDetection()
    })

    // Mouse events for desktop
    this.element.addEventListener("mousedown", (e) => {
      // Ignore right-clicks and middle-clicks
      if (e.button !== 0) return
      // If paused for share, don't start hold detection
      if (this.isPausedForShare) return
      this.startHoldDetection(e)
    })

    this.element.addEventListener("mouseup", (e) => {
      // If paused for share, click to resume
      if (this.isPausedForShare) {
        this.handleTapToResume(e)
        return
      }
      this.endHoldDetection()
    })

    this.element.addEventListener("mouseleave", () => {
      // End hold if mouse leaves the element while holding
      if (this.isPaused || this.holdDetectionTimer) {
        this.endHoldDetection()
      }
    })
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50
    const diff = startX - endX

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next story
        this.next()
      } else {
        // Swipe right - previous story
        this.previous()
      }
    }
  }

  // Hold-to-pause methods
  startHoldDetection(event) {
    // Don't start hold detection if clicking on interactive elements
    const target = event.target
    if (target.closest('button, a, [data-action*="share"]')) {
      return
    }

    this.cancelHoldDetection()
    this.holdStartTime = Date.now()

    this.holdDetectionTimer = setTimeout(() => {
      this.pauseStory()
    }, this.holdThreshold)
  }

  cancelHoldDetection() {
    if (this.holdDetectionTimer) {
      clearTimeout(this.holdDetectionTimer)
      this.holdDetectionTimer = null
    }
  }

  endHoldDetection() {
    this.cancelHoldDetection()

    // Track if we were holding so we can prevent click navigation
    this.wasHoldingBeforeRelease = this.isPaused

    if (this.isPaused) {
      this.resumeStory()
    }

    // Reset the flag after a short delay (after click events would have fired)
    setTimeout(() => {
      this.wasHoldingBeforeRelease = false
    }, 50)
  }

  pauseStory() {
    if (this.isPaused) return

    this.isPaused = true

    // Stop the auto-advance timer and calculate remaining time
    this.stopAutoAdvance()

    // Calculate how much time has elapsed since the story started
    const elapsed = Date.now() - this.progressStartTime
    const totalDuration = this.getCurrentAutoAdvanceDelay()
    this.remainingTime = Math.max(0, totalDuration - elapsed)

    // Pause the progress bar animation
    this.pauseProgressBar()
  }

  resumeStory() {
    if (!this.isPaused) return

    this.isPaused = false

    // Resume progress bar from where it was
    this.resumeProgressBar()

    // Restart auto-advance with remaining time
    this.startAutoAdvanceWithDuration(this.remainingTime)
  }

  pauseProgressBar() {
    const segment = this.progressSegmentTargets[this.currentIndexValue]
    if (!segment) return

    // Get the current computed width
    const computedStyle = getComputedStyle(segment)
    this.pausedProgressWidth = computedStyle.width

    // Freeze the animation by setting width to current value without transition
    segment.style.transition = "none"
    segment.style.width = this.pausedProgressWidth
  }

  resumeProgressBar() {
    const segment = this.progressSegmentTargets[this.currentIndexValue]
    if (!segment || this.remainingTime === null) return

    // Force reflow to ensure the paused state is applied
    void segment.offsetHeight

    // Resume animation to 100% over the remaining time
    requestAnimationFrame(() => {
      segment.style.transition = `width ${this.remainingTime}ms linear`
      segment.style.width = "100%"
    })
  }

  startAutoAdvanceWithDuration(duration) {
    if (duration <= 0) return

    this.stopAutoAdvance()

    // Update progress start time to reflect the resumed state
    this.progressStartTime = Date.now()

    this.autoAdvanceTimer = setTimeout(() => {
      if (this.currentIndexValue < this.totalStoriesValue - 1) {
        this.next()
      }
    }, duration)
  }

  next(event) {
    if (event) event.preventDefault()

    // Don't navigate if we just released from a hold
    if (this.wasHoldingBeforeRelease) return

    // Don't navigate if current story requires interaction
    if (this.requiresInteraction) return

    if (this.currentIndexValue < this.totalStoriesValue - 1) {
      this.currentIndexValue++
      const isRevisit = this.visitedStories.has(this.currentIndexValue)
      this.visitedStories.add(this.currentIndexValue)
      this.transition(isRevisit)
    } else {
      // At the end, exit
      this.exit()
    }
  }

  previous(event) {
    if (event) event.preventDefault()

    // Don't navigate if we just released from a hold
    if (this.wasHoldingBeforeRelease) return

    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      // Going back is always a revisit since we came from there
      this.transition(true)
    }
  }

  transition(isRevisit = false) {
    this.hideShareButton()
    this.showCurrentStory(isRevisit)
    this.updateBrowserHistory()

    // Track completion when landing on final story (only on first visit)
    if (!isRevisit && this.currentIndexValue === this.totalStoriesValue - 1) {
      this.trackEvent("completed")
    }

    // Check if this story requires interaction
    const currentStory = this.storyTargets[this.currentIndexValue]
    const storyRequiresInteraction = currentStory?.dataset.requiresInteraction === "true"

    // If story requires interaction and it's not a revisit, pause for user interaction
    // For revisits, the archetype should already be revealed, so proceed normally
    if (storyRequiresInteraction && !isRevisit) {
      // Set the flag so navigation is blocked until interaction
      this.requiresInteraction = true
      // Don't start auto-advance - will be started after reveal
      // Pause progress bar at a partial position
      this.pauseProgressBarForInteraction()
      // Don't show share button yet - will be shown after reveal
      return
    }

    // Reset interaction flag for normal stories
    this.requiresInteraction = false

    this.updateProgress()
    this.restartAutoAdvance()

    // Show share button immediately on revisit, otherwise schedule reveal
    if (isRevisit) {
      this.showShareButton()
    } else {
      this.scheduleShareButtonReveal()
    }
  }

  showCurrentStory(isRevisit = false) {
    // Hide all stories (but don't reset animations - they stay in completed state)
    this.storyTargets.forEach((story, index) => {
      if (index !== this.currentIndexValue) {
        story.style.opacity = "0"
        story.classList.add("pointer-events-none")
      }
    })

    // Show current story with fade in
    const currentStory = this.storyTargets[this.currentIndexValue]
    if (currentStory) {
      currentStory.classList.remove("pointer-events-none")

      // Fade in after a brief moment
      requestAnimationFrame(() => {
        currentStory.style.opacity = "1"

        // Only trigger animations if this is not a revisit
        if (!isRevisit) {
          const brushElement = currentStory.querySelector("[data-controller*='brush-stroke']")
          if (brushElement) {
            const brushController = this.application.getControllerForElementAndIdentifier(
              brushElement,
              "brush-stroke"
            )
            if (brushController) {
              brushController.replay()
            }
          }
        }

        // Handle archetype reveal on revisit - show revealed state immediately if already revealed
        if (isRevisit) {
          const archetypeElement = currentStory.querySelector("[data-controller*='archetype-reveal']")
          if (archetypeElement) {
            const archetypeController = this.application.getControllerForElementAndIdentifier(
              archetypeElement,
              "archetype-reveal"
            )
            // Only show revealed state if it was actually revealed before
            if (archetypeController && archetypeController.isRevealed) {
              archetypeController.showRevealed()
            }
          }
        }
      })

      // Track story view
      this.trackStoryView(this.currentIndexValue + 1)
    }
  }

  updateBrowserHistory() {
    const storyId = this.currentIndexValue + 1
    const newPath = `/year-in-review/${storyId}`

    if (window.history && window.history.pushState && window.location.pathname !== newPath) {
      window.history.pushState({ path: newPath, storyIndex: this.currentIndexValue }, '', newPath)
    }
  }

  exit() {
    // Navigate back or to root
    if (window.history.length > 1) {
      window.history.back()
    } else {
      window.location.href = "/"
    }
  }

  trackStoryView(storyId) {
    // Track via analytics if available
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "year_in_review_story_view",
        story_id: storyId
      })
    }
  }

  updateProgress() {
    // Update all progress segments
    this.progressSegmentTargets.forEach((segment) => {
      const segmentIndex = parseInt(segment.dataset.index)

      // Remove any existing transition first
      segment.style.transition = "none"

      if (segmentIndex < this.currentIndexValue) {
        // Completed stories - fill 100%
        segment.style.width = "100%"
      } else if (segmentIndex === this.currentIndexValue) {
        // Current story - reset to 0% then animate to 100%
        segment.style.width = "0%"

        // Force reflow to ensure the width reset is applied
        void segment.offsetHeight

        // Use requestAnimationFrame to ensure the animation starts after reset
        requestAnimationFrame(() => {
          segment.style.transition = `width ${this.getCurrentAutoAdvanceDelay()}ms linear`
          segment.style.width = "100%"
        })
      } else {
        // Future stories - empty
        segment.style.width = "0%"
      }
    })
  }

  startAutoAdvance() {
    this.stopAutoAdvance()

    const duration = this.getCurrentAutoAdvanceDelay()

    // Track when this story's progress started for pause/resume calculations
    this.progressStartTime = Date.now()
    this.remainingTime = duration

    this.autoAdvanceTimer = setTimeout(() => {
      if (this.currentIndexValue < this.totalStoriesValue - 1) {
        this.next()
      }
    }, duration)
  }

  stopAutoAdvance() {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer)
      this.autoAdvanceTimer = null
    }
  }

  restartAutoAdvance() {
    this.stopAutoAdvance()
    this.startAutoAdvance()
  }

  // Methods for stories that require interaction (like archetype reveal)
  // Note: setRequiresInteraction(true) is now called from transition() when entering
  // an interactive story. This method is kept for the archetype controller to call
  // setRequiresInteraction(false) when the reveal is complete.
  setRequiresInteraction(value) {
    this.requiresInteraction = value
  }

  pauseProgressBarForInteraction() {
    const segment = this.progressSegmentTargets[this.currentIndexValue]
    if (!segment) return

    // Stop the animation at a fixed position (e.g., 30% to show some progress)
    segment.style.transition = "none"
    segment.style.width = "30%"
  }

  onArchetypeRevealed() {
    // Called when user taps to reveal their archetype
    // Resume normal story behavior with remaining time
    this.requiresInteraction = false

    // Start progress from current position (30%) to 100%
    const segment = this.progressSegmentTargets[this.currentIndexValue]
    if (segment) {
      const remainingDuration = this.getCurrentAutoAdvanceDelay() * 0.7 // 70% of time remaining
      segment.style.transition = `width ${remainingDuration}ms linear`
      segment.style.width = "100%"
    }

    // Start auto-advance with remaining time
    const remainingTime = this.getCurrentAutoAdvanceDelay() * 0.7
    this.startAutoAdvanceWithDuration(remainingTime)

    // Show share button
    this.showShareButton()
  }

  // Share button reveal methods
  getRevealShareAfterDelay() {
    const currentStory = this.storyTargets[this.currentIndexValue]
    if (currentStory && currentStory.dataset.revealShareAfter) {
      return parseInt(currentStory.dataset.revealShareAfter, 10)
    }
    return 1000 // default 1 second
  }

  scheduleShareButtonReveal() {
    this.cancelShareButtonReveal()

    this.shareButtonTimer = setTimeout(() => {
      this.showShareButton()
    }, this.getRevealShareAfterDelay())
  }

  cancelShareButtonReveal() {
    if (this.shareButtonTimer) {
      clearTimeout(this.shareButtonTimer)
      this.shareButtonTimer = null
    }
  }

  showShareButton() {
    if (this.hasShareButtonTarget) {
      this.shareButtonTarget.style.opacity = "1"
    }
  }

  hideShareButton() {
    if (this.hasShareButtonTarget) {
      this.shareButtonTarget.style.opacity = "0"
    }
  }

  async share(event) {
    if (event) event.stopPropagation()

    // Check if we're in the native iOS app - let native handle screenshot + share
    if (document.documentElement.hasAttribute('data-turbo-native-app') && window.webkit?.messageHandlers?.nativeApp) {
      // Pause indefinitely until user taps to resume
      this.pauseForShare()
      window.webkit.messageHandlers.nativeApp.postMessage({ name: "share_screenshot" })
      return
    }

    // Fallback for web: share URL
    const shareUrl = window.location.href

    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Year in Skincare",
          text: "Check out my Year in Skincare on SkinSort!",
          url: shareUrl
        })
      } catch (err) {
        // User cancelled - ignore
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl)
      } catch (err) {
        // Clipboard failed - ignore
      }
    }
  }

  pauseForShare() {
    this.isPausedForShare = true
    this.pauseStory()
  }

  resumeFromShare() {
    if (!this.isPausedForShare) return

    this.isPausedForShare = false
    this.resumeStory()
  }

  // Called when user taps anywhere while paused for share
  handleTapToResume(event) {
    if (!this.isPausedForShare) return

    // Don't resume if tapping on interactive elements (let them handle their own action)
    const target = event.target
    if (target.closest('button, a')) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    this.resumeFromShare()
  }

  // Track analytics events (opened, completed) to the server
  async trackEvent(eventName) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch('/year-in-review/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ event: eventName })
      })
    } catch (e) {
      // Silently fail - analytics shouldn't break the experience
    }
  }
}
